/**
 * User-facing placeholder text and help copy for item form fields, keyed by
 * category. Categories not listed in `CATEGORY_FIELD_COPY` (e.g. TOY,
 * EVENT_PARTY) fall back to `DEFAULT_FIELD_COPY` automatically via
 * `getCategoryFieldCopy`.
 */
import type { ItemCategory } from '@/lib/itemCategoryConfig';

interface CategoryFieldCopy {
  namePlaceholder: string;
  nameHelpText: string;
  descriptionPlaceholder: string;
  brandPlaceholder?: string;
}

const DEFAULT_FIELD_COPY: CategoryFieldCopy = {
  namePlaceholder: 'e.g., Drill, Harry Potter book',
  nameHelpText: 'Use a clear, specific name so people can quickly recognize this item.',
  descriptionPlaceholder: 'Any details about the item',
  brandPlaceholder: 'e.g., DeWalt',
};

const CATEGORY_FIELD_COPY: Partial<Record<ItemCategory, CategoryFieldCopy>> = {
  TOOL: {
    namePlaceholder: 'e.g., Cordless drill',
    nameHelpText: 'Include the tool type and model so borrowers can identify the right one.',
    descriptionPlaceholder: 'e.g., 18V, includes two batteries and charger',
    brandPlaceholder: 'e.g., DeWalt',
  },
  BOOK: {
    namePlaceholder: 'e.g., The Hobbit',
    nameHelpText: 'Use the book title as the main name.',
    descriptionPlaceholder: 'e.g., Paperback fantasy novel by J.R.R. Tolkien',
  },
  BOARD_GAME: {
    namePlaceholder: 'e.g., Catan',
    nameHelpText: 'Use the game title people will search for.',
    descriptionPlaceholder: 'e.g., Strategy game for 3–4 players, ~60 min',
  },
  PUZZLE: {
    namePlaceholder: 'e.g., 1000-piece landscape puzzle',
    nameHelpText: 'Include piece count or theme to make it easy to spot.',
    descriptionPlaceholder: 'e.g., 1000 pieces, mountain scene, complete set',
  },
};

/**
 * Returns the copy config for a category, falling back to generic defaults
 * when no category-specific copy is defined (e.g. for TOY or EVENT_PARTY).
 * @param category
 */
export const getCategoryFieldCopy = (
  category: ItemCategory | '' | null | undefined,
): CategoryFieldCopy => {
  if (!category) return DEFAULT_FIELD_COPY;
  return CATEGORY_FIELD_COPY[category] ?? DEFAULT_FIELD_COPY;
};
